import { inject, Injectable } from '@angular/core'
import { AssistanceOccupation, AssistantAvailability } from '../../../models/assistantAvailability'
import { AssistanceStatusesService } from '../codelists/assistance-statuses.service'
import { defaultIfEmpty, first, map } from 'rxjs/operators'
import { forkJoin, Observable } from 'rxjs'
import { addMinutes } from 'date-fns'

@Injectable({
  providedIn: 'root',
})
export class AssistantAvailabilitiesOccupationService {
  private statusService = inject(AssistanceStatusesService)

  private TAG = 'AssistantAvailabilitiesOccupationService'

  private computeOccupationsFromAssignments(a: AssistantAvailability): Observable<AssistanceOccupation[]> {
    const negativeStatusIds$ = this.statusService.getAll().pipe(
      first(),
      map((ss) => ss.filter((s) => s.isNegative()).map((s) => s.id)),
    )

    return negativeStatusIds$.pipe(
      map((negativeStatusIds) => {
        const occupations: AssistanceOccupation[] = []

        if (a.assistantAssignments) {
          const seen = new Set<number>()
          // Iterate assignments (not unique assistances) to access transitDurationMinutes per assignment.
          for (const assignment of a.assistantAssignments.getAssignmentsPrivateArray()) {
            const assistance = assignment.assistance
            if (!assistance || seen.has(assistance.id)) continue
            seen.add(assistance.id)
            if (negativeStatusIds.includes(assistance.statusId as number)) continue
            const o = new AssistanceOccupation()
            o.dateFrom = assistance.getDateFrom()
            o.dateTo = addMinutes(assistance.getDateTo(), assignment.transitDurationMinutes ?? 0)
            o.client = assistance.client
            occupations.push(o)
          }
        }

        return occupations
      }),
    )
  }

  addOccupationsAndVacantTimes(a: AssistantAvailability): Observable<AssistantAvailability> {
    // const occupations = this.computeOccupationsFromAssignments(a)

    return this.computeOccupationsFromAssignments(a).pipe(
      map((occupations) => {
        a.assistanceOccupations = occupations

        a.remainingVacantTimes = a.getRemainingVacantTimes()

        if (a.assistanceOccupations) {
          for (let i = 0; i < a.assistanceOccupations.length; i++) {
            const o = new AssistanceOccupation()
            o.dateTo = a.assistanceOccupations[i].dateTo
            o.dateFrom = a.assistanceOccupations[i].dateFrom
            o.client = a.assistanceOccupations[i].client
            a.assistanceOccupations[i] = o
          }
        }
        return a
      }),
    )
  }

  addOccupationsAndVacantTimesMulti(aa: AssistantAvailability[]): Observable<AssistantAvailability[]> {
    const observables: Observable<AssistantAvailability>[] = []

    aa.map((a) => {
      observables.push(this.addOccupationsAndVacantTimes(a).pipe(first()))
    })

    return forkJoin(observables).pipe(defaultIfEmpty([]))
  }
}
